import type {
	AttributeType,
	ClassType,
	Container,
	Link,
	PageContainer,
	RelationType,
	Table,
	VisualElement,
} from "@judo/model-api";
import type { TabController } from "@judo/model-api";
import { buildMaskString } from "./build-mask-string";

/**
 * Build a `_mask` string for a table page or lazy relation table.
 *
 * Collects mask entries from:
 * - Column `attributeType`s (what the table displays)
 * - Column `representsRelation`s (inline single-aggregation columns → nested mask)
 * - Table `additionalMaskAttributes` / `additionalMaskRelations` (model-configured extras)
 * - PageContainer `additionalMaskAttributes` (page-level extras, for table pages only)
 *
 * @param table - The Table visual element
 * @param pageContainer - The PageContainer (pass only for table-type pages, not embedded tables)
 * @returns Mask string like `"{name,born,address{street,city}}"`
 *
 * @example
 * buildTableMask(table)
 * // => "{constellation,magnitude,nakedEye}"
 *
 * @example
 * buildTableMask(table, pageContainer)
 * // => "{constellation,magnitude,nakedEye,extraAttr}"
 */
export function buildTableMask(table: Table, pageContainer?: PageContainer): string {
	const attributes: AttributeType[] = [];
	const relations: RelationType[] = [];

	// Column attribute types
	for (const col of table.columns) {
		if (col.attributeType) {
			attributes.push(col.attributeType);
		}
		if (col.representsRelation) {
			relations.push(col.representsRelation);
		}
	}

	// Table additional mask attributes / relations
	if (table.additionalMaskAttributes) {
		attributes.push(...table.additionalMaskAttributes);
	}
	if (table.additionalMaskRelations) {
		relations.push(...table.additionalMaskRelations);
	}

	// Page container additional mask attributes (for table pages)
	if (pageContainer?.additionalMaskAttributes) {
		attributes.push(...pageContainer.additionalMaskAttributes);
	}

	return buildMaskString({ attributes, relations });
}

/**
 * Build a `_mask` string for a view page.
 *
 * Walks the entire visual element tree and collects:
 * - `attributeType` from Input, Formatted, and other attribute-bound elements
 * - Conditional attributes (`hiddenBy`, `enabledBy`, `requiredBy`)
 * - `titleAttribute` from the PageContainer
 * - `additionalMaskAttributes` from the PageContainer
 * - Eager Table column attributes (as nested relation masks)
 * - Eager Link part attributes (as nested relation masks)
 *
 * @param pageContainer - The root PageContainer of the view page
 * @param classType - Optional ClassType for the page's data element (to include representation attribute)
 * @returns Mask string like `"{name,born,stars{constellation,magnitude}}"`
 *
 * @example
 * buildViewMask(pageContainer)
 * // => "{name,born,hidden,stars{constellation,magnitude}}"
 */
export function buildViewMask(pageContainer: PageContainer, classType?: ClassType): string {
	const attributes: AttributeType[] = [];
	const eagerRelationParts: string[] = [];

	// Walk the visual tree
	collectFromVisualElement(pageContainer, attributes, eagerRelationParts);

	// Page container additional mask attributes
	if (pageContainer.additionalMaskAttributes) {
		attributes.push(...pageContainer.additionalMaskAttributes);
	}

	// Page title attribute
	if (pageContainer.titleAttribute) {
		attributes.push(pageContainer.titleAttribute);
	}

	// ClassType representation attribute (used for breadcrumb / page title display)
	if (classType?.representation) {
		attributes.push(classType.representation);
	}

	// Deduplicate attributes by name
	const uniqueNames = new Set<string>();
	const parts: string[] = [];
	for (const attr of attributes) {
		if (attr.name && !uniqueNames.has(attr.name)) {
			uniqueNames.add(attr.name);
			parts.push(attr.name);
		}
	}

	// Add eager relation masks (already formatted as "relName{attr1,attr2}")
	parts.push(...eagerRelationParts);

	return `{${parts.join(",")}}`;
}

/**
 * Recursively collect attribute references and eager relation masks
 * from a visual element tree.
 */
function collectFromVisualElement(
	element: VisualElement,
	attributes: AttributeType[],
	eagerRelationParts: string[]
): void {
	// Conditional attributes from any visual element
	if (element.hiddenBy) attributes.push(element.hiddenBy);
	if (element.enabledBy) attributes.push(element.enabledBy);
	if (element.requiredBy) attributes.push(element.requiredBy);

	// attributeType on Input, Formatted, etc.
	const attrBased = element as VisualElement & { attributeType?: AttributeType };
	if (attrBased.attributeType) {
		attributes.push(attrBased.attributeType);
	}

	// Handle eager Table — contribute as nested relation mask
	if (element["@type"] === "Table") {
		const table = element as unknown as Table;
		if (String(table.isEager) === "true") {
			const relation = table.dataElement as RelationType | undefined;
			if (relation?.name) {
				const innerMask = buildTableInnerMask(table);
				eagerRelationParts.push(innerMask ? `${relation.name}{${innerMask}}` : `${relation.name}{}`);
			}
		}
	}

	// Handle eager Link — contribute as nested relation mask
	if (element["@type"] === "Link") {
		const link = element as unknown as Link;
		if (String(link.isEager) === "true") {
			const relation = link.dataElement as RelationType | undefined;
			if (relation?.name) {
				const innerMask = buildLinkInnerMask(link);
				eagerRelationParts.push(innerMask ? `${relation.name}{${innerMask}}` : `${relation.name}{}`);
			}
		}
	}

	// Recurse into Container children
	const container = element as unknown as Container;
	if (Array.isArray(container.children)) {
		for (const child of container.children) {
			collectFromVisualElement(child, attributes, eagerRelationParts);
		}
	}

	// Recurse into TabController tabs
	if (element["@type"] === "TabController") {
		const tabController = element as unknown as TabController;
		if (Array.isArray(tabController.tabs)) {
			for (const tab of tabController.tabs) {
				if (tab.element) {
					collectFromVisualElement(tab.element as VisualElement, attributes, eagerRelationParts);
				}
			}
		}
	}
}

/**
 * Build the inner mask content for a table (without outer braces or relation name).
 * Used for eager table contributions to view masks.
 */
function buildTableInnerMask(table: Table): string {
	const parts: string[] = [];
	const seen = new Set<string>();

	for (const col of table.columns) {
		if (col.attributeType?.name && !seen.has(col.attributeType.name)) {
			seen.add(col.attributeType.name);
			parts.push(col.attributeType.name);
		}
		if (col.representsRelation?.name) {
			const rel = col.representsRelation;
			const targetAttrs = (rel.target?.attributes ?? []).map((a) => a.name).filter(Boolean);
			parts.push(targetAttrs.length > 0 ? `${rel.name}{${targetAttrs.join(",")}}` : `${rel.name}{}`);
		}
	}

	for (const attr of table.additionalMaskAttributes ?? []) {
		if (attr.name && !seen.has(attr.name)) {
			seen.add(attr.name);
			parts.push(attr.name);
		}
	}

	for (const rel of table.additionalMaskRelations ?? []) {
		if (rel.name) {
			const targetAttrs = (rel.target?.attributes ?? []).map((a) => a.name).filter(Boolean);
			parts.push(targetAttrs.length > 0 ? `${rel.name}{${targetAttrs.join(",")}}` : `${rel.name}{}`);
		}
	}

	return parts.join(",");
}

/**
 * Build the inner mask content for a link (without outer braces or relation name).
 * Used for eager link contributions to view masks.
 */
function buildLinkInnerMask(link: Link): string {
	const parts: string[] = [];
	const seen = new Set<string>();

	// Link.parts are Column elements (used for displaying selected value)
	for (const part of link.parts ?? []) {
		if (part.attributeType?.name && !seen.has(part.attributeType.name)) {
			seen.add(part.attributeType.name);
			parts.push(part.attributeType.name);
		}
	}

	for (const attr of link.additionalMaskAttributes ?? []) {
		if (attr.name && !seen.has(attr.name)) {
			seen.add(attr.name);
			parts.push(attr.name);
		}
	}

	for (const rel of link.additionalMaskRelations ?? []) {
		if (rel.name) {
			const targetAttrs = (rel.target?.attributes ?? []).map((a) => a.name).filter(Boolean);
			parts.push(targetAttrs.length > 0 ? `${rel.name}{${targetAttrs.join(",")}}` : `${rel.name}{}`);
		}
	}

	return parts.join(",");
}
